import {ElementRef} from "@angular/core";
import {MatDialogRef} from "@angular/material/dialog";

export type Step = (() => void) | number | AnimationStep | Sequence | (() => Promise<void>);

export class Sequence {
  constructor(private readonly steps: Step[], private readonly defaultWaitAfterStep?: number) {
  }

  public async execute(): Promise<void> {
    for (const step of this.steps) {
      if (typeof step === 'number') {
        await this.awaitTime(step);
      } else if (step instanceof AnimationStep) {
        step.execute();
        if (step.options.wait) {
          await this.awaitTime(1000);
        }
      } else if (step instanceof Sequence) {
        await step.execute();
      } else {
        await step();
        await this.awaitTime(this.defaultWaitAfterStep);
      }
    }
  }

  private async awaitTime(time?: number) {
    if (!time) {
      return;
    }
    await new Promise(resolve => setTimeout(resolve, time));
  }
}

export class AnimationStep {
  options: AnimationOptions = {
    type: AnimationType.ENTER,
    animation: 'bounceInDown',
    wait: false
  }

  constructor(private el?: ElementRef, options?: Partial<AnimationOptions>) {
    Object.assign(this.options, options);

    if (this.options.type === AnimationType.ENTER && this.el && this.el.nativeElement) {
      this.el.nativeElement.style.visibility = 'hidden';
    }
  }

  execute() {
    if (!this.el || !this.el.nativeElement) {
      return;
    }
    if (this.options.type === AnimationType.ENTER) {
      this.el.nativeElement.style.visibility = 'visible';
    }
    this.el.nativeElement.classList.add('animate__animated', 'animate__' + this.options.animation);
    setTimeout(() => {
      this.el!.nativeElement.classList.remove('animate__animated', 'animate__' + this.options.animation);
    }, 1000)
  }
}

export interface AnimationOptions {
  type: AnimationType;
  animation: 'bounceIn' | 'bounceInDown' | 'zoomIn' | 'shakeY' | 'shakeX';
  wait: boolean;
}

export enum AnimationType {
  ENTER, EXIT, NONE
}

export class HideDialogStep extends Sequence {
  constructor(ref: MatDialogRef<any>) {
    super([
      () => ref.addPanelClass('animate__bounceOutUp'),
      500,
      () => ref.close()
    ])
  }
}

export class BackdropClassStep extends Sequence {
  constructor(private className: string, private add = true) {
    super([
      () => {
        if (add) {
          document.querySelector('.cdk-overlay-backdrop')?.classList.add(this.className)
        } else {
          document.querySelector('.cdk-overlay-backdrop')?.classList.remove(this.className)
        }
      }
    ])
  }
}

export class AddCssClassStep extends Sequence {
  constructor(el: ElementRef, className: string, removeAfter?: number) {
    super([
      () => el.nativeElement?.classList.add(className),
      ...(removeAfter ? [removeAfter, () => el.nativeElement?.classList.remove(className)] : [])
    ])
  }
}
